import { fromJS, List, Set as ImmutableSet } from 'immutable';
import Actions from 'flow/actions';
import {
    getShowIncomingVideo,
    getShowLocalVideoInGrid,
    getVideoBackgroundPreferences,
} from 'flow/utils/browser-utils';
import {
    BLUR_AMOUNT,
    EFFECT_MODE,
} from 'flow/constants/video-background-constants';

// Get default blur amount from environment variable
const getDefaultBlurAmount = () => {
    const envBlurAmount = process.env.VIDEO_BLUR_AMOUNT;
    const defaultValue = envBlurAmount
        ? parseInt(envBlurAmount, 10)
        : BLUR_AMOUNT.DEFAULT;
    // Ensure the value is within valid range
    return Math.max(BLUR_AMOUNT.MIN, Math.min(BLUR_AMOUNT.MAX, defaultValue));
};

// Load saved background preferences or use defaults
const getInitialBackgroundPreferences = () => {
    const saved = getVideoBackgroundPreferences();
    return {
        mode: saved?.mode || EFFECT_MODE.NONE,
        imageUrl: saved?.imageUrl || null,
    };
};

const initialBackgroundPrefs = getInitialBackgroundPreferences();

const initialState = fromJS({
    webSocket: null,
    joinRoomPromise: null,
    sender: {
        videoStream: null,
        secondaryVideoStream: null,
        primaryPeer: null,
        secondaryPeer: null,
        videoActive: true,
        screenShareActive: false,
        screenStream: null,
        reconnectionToken: null,
    },
    showLocalVideoInGrid: getShowLocalVideoInGrid(),
    showScreenShareInGrid: true,
    // When false, incoming (remote) camera feeds are not rendered — the viewer
    // can turn video off to reduce decoding load. Persisted per browser.
    showIncomingVideo: getShowIncomingVideo(),
    // True aspect ratio (w/h) of the shared screen, measured from the on-stage
    // video; null when nothing is shared. Drives the stage's shape so a
    // non-landscape share isn't forced into a 16:9 box.
    screenShareAspect: null,
    // A Set of pinned feed display names: unordered, unique membership with
    // O(1) lookup (the pinned stage renders in feed order, not pin order).
    // fromJS leaves an existing Immutable collection untouched.
    pinnedFeedNames: ImmutableSet(),
    videoMembers: {},
    activityList: [],
    // Member names the backend reports as currently speaking (VID-222). A Set
    // for O(1) per-tile lookup, mirroring pinnedFeedNames.
    speakingMembers: ImmutableSet(),
    // Locally-detected own-user speaking state, driving the own tile's highlight
    // directly (instant, no backend round-trip) — see useOwnSpeakingDetection.
    ownUserIsSpeaking: false,
    // Everyone the server is currently holding in the waiting room (VID-76), as
    // their join names (`<uuid>-<nick>`). A List because the panel renders it in
    // the order the server sends; membership lookups aren't needed here.
    waitingParticipants: [],
    // Our own standing in that waiting room — see WaitingRoomStanding. `state` stays
    // null for anyone who was never held back.
    ownWaitingRoom: {
        state: null,
        cause: null,
    },
    connectionState: null,
    backgroundBlur: {
        blurAmount: getDefaultBlurAmount(),
    },
    backgroundEffect: {
        mode: initialBackgroundPrefs.mode,
        imageUrl: initialBackgroundPrefs.imageUrl,
    },
});

// eslint-disable-next-line complexity
const videoReducer = (state = initialState, action) => {
    switch (action ? action.type : null) {
        case Actions.CLEAR_STATE: {
            return initialState;
        }
        case Actions.VIDEO_SET_WEBSOCKET: {
            const webSocket = action && action.webSocket;
            return state.set('webSocket', webSocket);
        }
        case Actions.VIDEO_SET_JOIN_ROOM_PROMISE: {
            const promise = action && action.promise;
            return state.set('joinRoomPromise', promise);
        }
        case Actions.VIDEO_SET_VIDEO_SENDER_STREAM: {
            const stream = action && action.stream;
            return state.setIn(['sender', 'videoStream'], stream);
        }
        case Actions.VIDEO_SET_VIDEO_SECONDARY_SENDER_STREAM: {
            const secondaryStream = action && action.secondaryStream;
            return state.setIn(
                ['sender', 'secondaryVideoStream'],
                secondaryStream,
            );
        }
        case Actions.VIDEO_SET_VIDEO_SENDER_PRIMARY_PEER: {
            const primaryPeer = action && action.primaryPeer;
            return state.setIn(['sender', 'primaryPeer'], primaryPeer);
        }
        case Actions.VIDEO_SET_VIDEO_SENDER_SECONDARY_PEER: {
            const secondaryPeer = action && action.secondaryPeer;
            return state.setIn(['sender', 'secondaryPeer'], secondaryPeer);
        }
        case Actions.VIDEO_SET_SENDER_VIDEO_ACTIVE_STATUS: {
            const videoActive = action && action.videoActive;
            return state.setIn(['sender', 'videoActive'], videoActive);
        }
        case Actions.VIDEO_SET_SENDER_SCREEN_SHARE_ACTIVE: {
            const screenShareActive = action && action.screenShareActive;
            return state.setIn(
                ['sender', 'screenShareActive'],
                screenShareActive,
            );
        }
        case Actions.VIDEO_SET_SENDER_SCREEN_STREAM: {
            const screenStream = action && action.screenStream;
            return state.setIn(['sender', 'screenStream'], screenStream);
        }
        case Actions.VIDEO_SET_SCREEN_SHARE_ASPECT: {
            const aspect = action && action.aspect;
            return state.set('screenShareAspect', aspect ?? null);
        }
        case Actions.VIDEO_ADD_VIDEO_MEMBER: {
            const name = action && action.memberName;
            const remoteMeetingMember = new fromJS({
                name,
                primaryPeer: {},
                secondaryPeer: null,
                videoActive: true,
                audioActive: true,
                screenShareActive: false,
                peerSetupAttempts: 0,
            });
            // Registration must not destroy live state: if the member already
            // has a record (e.g. a receiver peer was stored before
            // `existingParticipants` re-announced them), replacing it would
            // orphan the live peer behind a fresh `primaryPeer: {}` placeholder
            // — the SDP answer / ICE candidates for that peer then have nowhere
            // to land and the tile is stuck on the avatar. Merge instead:
            // existing values (peers, track flags) win, the defaults above only
            // fill gaps. A genuinely new member still gets the full fresh
            // record; members who leave are removed (removeVideoMemberAction),
            // so a rejoin starts fresh too.
            return state.update('videoMembers', (videoMembers) => {
                const existing = videoMembers.get(name);
                return videoMembers.set(
                    name,
                    existing
                        ? remoteMeetingMember.merge(existing)
                        : remoteMeetingMember,
                );
            });
        }
        case Actions.VIDEO_SET_SHOW_LOCAL_VIDEO_IN_GRID: {
            const shouldShowInGrid = action && action.shouldShowInGrid;
            return state.set('showLocalVideoInGrid', shouldShowInGrid);
        }
        case Actions.VIDEO_SET_SHOW_SCREEN_SHARE_IN_GRID: {
            const shouldShowInGrid =
                action && action.shouldShowScreenShareInGrid;
            return state.set('showScreenShareInGrid', shouldShowInGrid);
        }
        case Actions.VIDEO_SET_SHOW_INCOMING_VIDEO: {
            const showIncomingVideo = action && action.showIncomingVideo;
            return state.set('showIncomingVideo', showIncomingVideo);
        }
        case Actions.VIDEO_SET_VIDEO_MEMBER_SETUP_ATTEMPTS: {
            const name = action && action.memberName;
            const setupAttempts = action && action.setupAttempts;
            return state.setIn(
                ['videoMembers', name, 'peerSetupAttempts'],
                setupAttempts,
            );
        }
        case Actions.VIDEO_REMOVE_VIDEO_MEMBER: {
            const memberName = action && action.memberName;
            return state
                .update('videoMembers', (videoMembers) =>
                    videoMembers.delete(memberName),
                )
                .update('pinnedFeedNames', (names) => names.delete(memberName));
        }
        case Actions.VIDEO_PIN_FEED: {
            const name = action && action.feedName;
            return state.update('pinnedFeedNames', (names) => names.add(name));
        }
        case Actions.VIDEO_UNPIN_FEED: {
            const name = action && action.feedName;
            return state.update('pinnedFeedNames', (names) =>
                names.delete(name),
            );
        }
        case Actions.VIDEO_CLEAR_PINNED_FEEDS: {
            return state.set('pinnedFeedNames', ImmutableSet());
        }
        case Actions.VIDEO_CLEAR_VIDEO_MEMBERS: {
            // Pins reference members, so clearing every member clears every pin
            // (mirrors VIDEO_REMOVE_VIDEO_MEMBER pruning a single one).
            return state
                .set('videoMembers', fromJS({}))
                .set('pinnedFeedNames', ImmutableSet());
        }
        case Actions.MEETING_SET_ACTIVE_MEETING_MEMBERS: {
            // Prune pins for participants who have left. A pin can reference any
            // feed member — including dial-in callers, which never appear in
            // videoMembers (so VIDEO_REMOVE_VIDEO_MEMBER never prunes them) — so
            // reconcile against the full meeting-member list, the authoritative
            // present set. Guards keep the common polling path a no-op and avoid
            // wiping pins on a transient/empty update (teardown clears via
            // CLEAR_STATE).
            const members = action && action.conferenceMembers;
            const pinnedFeedNames = state.get('pinnedFeedNames');
            if (!members || members.isEmpty() || pinnedFeedNames.isEmpty()) {
                return state;
            }
            const presentNames = members
                .map((member) => member.get('displayName'))
                .toSet();
            const prunedPins = pinnedFeedNames.filter((name) =>
                presentNames.has(name),
            );
            return prunedPins.size === pinnedFeedNames.size
                ? state
                : state.set('pinnedFeedNames', prunedPins);
        }
        case Actions.VIDEO_SET_MEMBER_PRIMARY_PEER: {
            const memberName = action && action.memberName;
            const memberPrimaryPeer = action && action.memberPrimaryPeer;
            return state.setIn(
                ['videoMembers', memberName, 'primaryPeer'],
                memberPrimaryPeer,
            );
        }
        case Actions.VIDEO_SET_MEMBER_SECONDARY_PEER: {
            const memberName = action && action.memberName;
            const memberSecondaryPeer = action && action.memberSecondaryPeer;
            return state.setIn(
                ['videoMembers', memberName, 'secondaryPeer'],
                memberSecondaryPeer,
            );
        }
        case Actions.VIDEO_SET_MEMBER_TRACK_ACTIVE_STATUS: {
            const memberName = action && action.memberName;
            const trackType = action && action.trackType;
            const active = action && action.active;
            return state.setIn(['videoMembers', memberName, trackType], active);
        }
        case Actions.VIDEO_SET_NEW_ACTIVITY: {
            const activityList = action && action.activityList;
            return state.set('activityList', activityList);
        }
        case Actions.VIDEO_SET_SPEAKING_MEMBERS: {
            const speakingMembers = action && action.speakingMembers;
            return state.set('speakingMembers', ImmutableSet(speakingMembers));
        }
        case Actions.VIDEO_SET_OWN_USER_IS_SPEAKING: {
            const ownUserIsSpeaking = action && action.ownUserIsSpeaking;
            return state.set('ownUserIsSpeaking', !!ownUserIsSpeaking);
        }
        case Actions.VIDEO_SET_WAITING_PARTICIPANTS: {
            const waitingParticipants = action && action.waitingParticipants;
            return state.set(
                'waitingParticipants',
                waitingParticipants || List(),
            );
        }
        case Actions.VIDEO_SET_OWN_WAITING_ROOM_STANDING: {
            const waitingRoomStanding = action && action.waitingRoomStanding;
            const cause = action && action.cause;
            return state.set(
                'ownWaitingRoom',
                fromJS({
                    state: waitingRoomStanding || null,
                    cause: cause || null,
                }),
            );
        }
        case Actions.VIDEO_SET_CONNECTION_STATE: {
            const connectionState = action && action.connectionState;
            return state.set('connectionState', connectionState);
        }
        case Actions.VIDEO_SET_SENDER_RECONNECTION_TOKEN: {
            const reconnectionToken = action && action.reconnectionToken;
            return state.setIn(
                ['sender', 'reconnectionToken'],
                reconnectionToken,
            );
        }
        case Actions.VIDEO_SET_BACKGROUND_BLUR_AMOUNT: {
            const blurAmount = action && action.blurAmount;
            return state.setIn(['backgroundBlur', 'blurAmount'], blurAmount);
        }
        case Actions.VIDEO_SET_BACKGROUND_IMAGE: {
            const imageUrl = action && action.imageUrl;
            return state.setIn(['backgroundEffect', 'imageUrl'], imageUrl);
        }
        case Actions.VIDEO_SET_BACKGROUND_MODE: {
            const mode = action && action.mode;
            return state.setIn(['backgroundEffect', 'mode'], mode);
        }
        default: {
            return state;
        }
    }
};

export default videoReducer;
